import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ProfileSuccessCodes } from '@app/translations';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  UiZoneWidgetAttributeConfig,
  UserMarketingPreference,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { ClearFormDataActionWithId } from '../../../actions/form-submission.action';
import { UpdateUserMarketingPreferencesInProfileState } from '../../../actions/user-profile.action';
import { MetadataService } from '../../../services/metadata-service/metadata-service.service';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { FormActionState } from '../../../state/form-submision.state';
import { ProfileState } from '../../../state/user-profile.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { handleRedirection } from '../cta-redirection.utils';

export function updateMarketingPreferences(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const alertHandlerService = injector.get(AlertHandlerService);
  const store = injector.get(Store);
  const workflowFormsService = injector.get(WorkflowFormsService);
  const metadataService = injector.get(MetadataService);
  const router = injector.get(Router);
  const marketingPreferences = store.selectSnapshot(ProfileState.getMarketingPreferences);
  const formValues =
    store.selectSnapshot(FormActionState.getFormActionStateWithId(FormID.MARKETING_PREFERENCES_FORM))
      ?.formData ?? {};
  if (!Object.keys(formValues).length || !formValues?.marketingPreferences) {
    console.error('Marketing preferences not found');
    return;
  }

  const payload: UserMarketingPreference = {} as UserMarketingPreference;
  Object.keys(marketingPreferences).forEach((key) => {
    payload[key as keyof UserMarketingPreference] = !!formValues.marketingPreferences.includes(key);
  });

  const actionNavigationConfigurations = attri.widgetProperties?.actionNavigationConfigurations;

  metadataService
    .updateUserProfileData({ marketingPreferences: payload })
    .pipe(
      tap((response) => {
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          ProfileSuccessCodes.MARKETING_PREFERENCE_UPDATED,
        ]);
        if (response?.marketingPreferences)
          store.dispatch(new UpdateUserMarketingPreferencesInProfileState(response?.marketingPreferences));
        if (actionNavigationConfigurations?.success) {
          handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true);
          store.dispatch(new ClearFormDataActionWithId(FormID.MARKETING_PREFERENCES_FORM));
        }
      }),
      catchError((err) => {
        console.error('Failed to update marketing preferences with this error:', err);
        resetMarketingPreferencesForm(marketingPreferences, workflowFormsService);
        if (actionNavigationConfigurations?.failure) {
          handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
          store.dispatch(new ClearFormDataActionWithId(FormID.MARKETING_PREFERENCES_FORM));
        }
        throw err;
      }),
    )
    .subscribe();
}

function resetMarketingPreferencesForm(
  marketingPreferences: UserMarketingPreference,
  workflowFormsService: WorkflowFormsService,
) {
  const currentPageForm = workflowFormsService.getCurrentPageForms()[0];
  const formGroup = workflowFormsService.getFormPart(currentPageForm);
  if (!formGroup) return;
  const valuesToPatch = Object.keys(marketingPreferences)
    .map((key) => {
      return marketingPreferences[key as keyof UserMarketingPreference] === true ? key : null;
    })
    .filter((key) => key !== null);
  formGroup.controls['marketingPreferences']?.patchValue(valuesToPatch);
}
